import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import type { ApolloCache } from "@apollo/client";

import {
  CREATE_AUTOMATION_MUTATION,
  DELETE_AUTOMATION_MUTATION,
  SET_AUTOMATION_ACTIVE_MUTATION,
  SUGGEST_AUTOMATION_TYPE_MUTATION,
  UPDATE_AUTOMATION_MUTATION,
} from "~/api/_mutations";
import {
  GET_AUTOMATION_QUERY,
  LIST_AUTOMATION_NOTIFICATION_USERS_QUERY,
  LIST_AUTOMATIONS_QUERY,
} from "~/api/_queries";
import type {
  Automation,
  AutomationSuggestSection,
  CreateAutomationInput,
  UpdateAutomationInput,
} from "~/types/automation";
import { errorsHandler } from "~/transformers";

const INTERNAL_OEM_ROLES = new Set(["OWNER", "OEM", "STAFF", "TECHNICIAN"]);

type AutomationNotificationUser = {
  _id: string;
  name?: string | null;
  email?: string | null;
  role?: string | null;
  facility?: { _id: string } | null;
};

const upsertAutomationInListCache = (
  cache: ApolloCache<unknown>,
  automation: Automation,
) => {
  try {
    const existing = cache.readQuery<{ listAutomations: Automation[] }>({
      query: LIST_AUTOMATIONS_QUERY,
    });
    const list = existing?.listAutomations ?? [];
    const idx = list.findIndex((item) => item._id === automation._id);
    const next =
      idx >= 0
        ? list.map((item, i) => (i === idx ? automation : item))
        : [automation, ...list];
    cache.writeQuery({
      query: LIST_AUTOMATIONS_QUERY,
      data: { listAutomations: next },
    });
  } catch {
    // list not in cache yet
  }
};

const removeAutomationFromListCache = (
  cache: ApolloCache<unknown>,
  id: string,
) => {
  try {
    const existing = cache.readQuery<{ listAutomations: Automation[] }>({
      query: LIST_AUTOMATIONS_QUERY,
    });
    if (!existing?.listAutomations) return;
    cache.writeQuery({
      query: LIST_AUTOMATIONS_QUERY,
      data: {
        listAutomations: existing.listAutomations.filter(
          (item) => item._id !== id,
        ),
      },
    });
  } catch {
    // list not in cache yet
  }
};

export const useListAutomations = (skip = false) => {
  const { data, loading, error, refetch } = useQuery<{
    listAutomations: Automation[];
  }>(LIST_AUTOMATIONS_QUERY, { skip });

  return {
    automations: data?.listAutomations ?? [],
    loading,
    error,
    refetch,
  };
};

/** Users BE allows for sendNotification: OEM roles, no facility. */
export const useListAutomationNotificationUsers = () => {
  const { data, loading, error } = useQuery<{
    listOwnOemSupportAccounts: AutomationNotificationUser[];
  }>(LIST_AUTOMATION_NOTIFICATION_USERS_QUERY, {
    variables: { getAllUsers: true },
  });

  const users = (data?.listOwnOemSupportAccounts ?? []).filter(
    (user) => INTERNAL_OEM_ROLES.has(user.role ?? "") && !user.facility?._id,
  );

  return { users, loading, error };
};

export const useGetAutomation = (id?: string) => {
  const { data, loading, error, refetch } = useQuery<{
    getAutomation: Automation | null;
  }>(GET_AUTOMATION_QUERY, {
    variables: { id },
    skip: !id,
  });

  return {
    automation: data?.getAutomation ?? null,
    loading,
    error,
    refetch,
  };
};

export const useLazyGetAutomation = () => {
  const [load, result] = useLazyQuery<{ getAutomation: Automation | null }>(
    GET_AUTOMATION_QUERY,
  );
  return {
    loadAutomation: (id: string) => load({ variables: { id } }),
    ...result,
  };
};

export const useCreateAutomation = () => {
  const [mutate, { loading }] = useMutation(CREATE_AUTOMATION_MUTATION);

  return {
    createAutomation: (input: CreateAutomationInput) =>
      new Promise(async (resolve, reject) => {
        try {
          const result = await mutate({
            variables: { input },
            update(cache, { data }) {
              const automation = data?.createAutomation;
              if (automation) upsertAutomationInListCache(cache, automation);
            },
            refetchQueries: [{ query: LIST_AUTOMATIONS_QUERY }],
            awaitRefetchQueries: true,
          });
          resolve(result);
        } catch (err) {
          reject(errorsHandler(err));
        }
      }),
    loading,
  };
};

export const useUpdateAutomation = () => {
  const [mutate, { loading }] = useMutation(UPDATE_AUTOMATION_MUTATION);

  return {
    updateAutomation: (input: UpdateAutomationInput) =>
      new Promise(async (resolve, reject) => {
        try {
          const result = await mutate({
            variables: { input },
            update(cache, { data }) {
              const automation = data?.updateAutomation;
              if (automation) upsertAutomationInListCache(cache, automation);
            },
            refetchQueries: [{ query: LIST_AUTOMATIONS_QUERY }],
            awaitRefetchQueries: true,
          });
          resolve(result);
        } catch (err) {
          reject(errorsHandler(err));
        }
      }),
    loading,
  };
};

export const useSetAutomationActive = () => {
  const [mutate, { loading }] = useMutation(SET_AUTOMATION_ACTIVE_MUTATION);

  return {
    setAutomationActive: (id: string, active: boolean) =>
      new Promise(async (resolve, reject) => {
        try {
          const result = await mutate({
            variables: { id, active },
            update(cache, { data }) {
              const automation = data?.setAutomationActive;
              if (automation) upsertAutomationInListCache(cache, automation);
            },
          });
          resolve(result);
        } catch (err) {
          reject(errorsHandler(err));
        }
      }),
    loading,
  };
};

export const useDeleteAutomation = () => {
  const [mutate, { loading }] = useMutation(DELETE_AUTOMATION_MUTATION);

  return {
    deleteAutomation: (id: string) =>
      new Promise(async (resolve, reject) => {
        try {
          const result = await mutate({
            variables: { id },
            update(cache) {
              removeAutomationFromListCache(cache, id);
            },
            refetchQueries: [{ query: LIST_AUTOMATIONS_QUERY }],
            awaitRefetchQueries: true,
          });
          resolve(result);
        } catch (err) {
          reject(errorsHandler(err));
        }
      }),
    loading,
  };
};

export const useSuggestAutomationType = () => {
  const [mutate, { loading }] = useMutation(SUGGEST_AUTOMATION_TYPE_MUTATION);

  return {
    suggestAutomationType: (input: {
      section: AutomationSuggestSection;
      whatWouldYouLike: string;
      whyImportant: string;
    }) =>
      new Promise(async (resolve, reject) => {
        try {
          const result = await mutate({ variables: { input } });
          resolve(result);
        } catch (err) {
          reject(errorsHandler(err));
        }
      }),
    loading,
  };
};
